/**
 * ImageBuildDetailPage — single-build polling view (NC-498 Phase 3 + 4).
 *
 * Polls `GET /api/images/{buildId}` every 30 s. Surfaces status, duration,
 * gallery-image-version on success, and an expandable error-tail on
 * failure. Phase 4 adds the admin "Promote to Production" affordance.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CloudUpload as PromoteIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatDateSafe } from '../utils/dateFormat';

import { imageBuildsApi } from '../services/imageBuildsService';
import { ImageBuildDoc, ImageBuildStatus } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import ErrorMessage from '../components/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { isPromotable, isStagingOnly } from '../config/workloadCapabilities';

const REFETCH_INTERVAL_MS = 30_000;

const STATUS_COLOR: Record<
  ImageBuildStatus,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  queued: 'info',
  running: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const STATUS_LABEL: Record<ImageBuildStatus, string> = {
  queued: 'Wartet auf Runner',
  running: 'Läuft',
  completed: 'Fertig',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
};

function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest === 0 ? `${minutes}min` : `${minutes}min ${rest}s`;
  const hours = Math.floor(minutes / 60);
  const mRest = minutes % 60;
  return `${hours}h ${mRest}min`;
}

function formatTimestamp(iso: string | undefined): string {
  // formatDateSafe guards parseISO(undefined); keep the prior raw-string
  // fallback for non-empty-but-unparseable values (NC-939).
  const formatted = formatDateSafe(iso, 'dd.MM.yyyy HH:mm:ss');
  return formatted === '—' && iso ? iso : formatted;
}

export default function ImageBuildDetailPage() {
  const { buildId } = useParams<{ buildId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workloadIdHint = searchParams.get('workloadId') ?? undefined;
  const { hasRole } = useAuth();

  // Phase 4 — admin-only promote flow state (dialog + inflight + error).
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteSubmitting, setPromoteSubmitting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useApiQuery<ImageBuildDoc>(
    () => imageBuildsApi.getById(buildId ?? '', workloadIdHint),
    [buildId, workloadIdHint],
    { enabled: !!buildId }
  );

  // Polling — only while the build is in a non-terminal state. Once it ends,
  // there's no point refetching every 30 s and we'd just hammer the API.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const isTerminal = data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled';

  useEffect(() => {
    if (isTerminal) return;
    const id = setInterval(() => {
      refetchRef.current();
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isTerminal]);

  const headline = useMemo(() => {
    if (!data) return `Image-Build ${buildId?.slice(0, 8) ?? ''}`;
    return `Image-Build ${data.workloadId} · ${data.id.slice(0, 8)}`;
  }, [data, buildId]);

  if (!buildId) {
    return (
      <Box p={2}>
        <ErrorMessage message="Keine buildId in der URL." title="Fehler" />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }} aria-label="back">
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {headline}
        </Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={refetch} aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <ErrorMessage message={error} title="Fehler beim Laden des Builds" onRetry={refetch} />}

      {loading && !data && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Chip
                label={STATUS_LABEL[data.status]}
                color={STATUS_COLOR[data.status]}
                size="medium"
              />
              <Chip label={data.tier} size="small" variant="outlined" />
              <Chip label={data.environment} size="small" variant="outlined" />
              {data.dryRun && (
                <Chip
                  label="Dry-Run"
                  size="small"
                  color="warning"
                  variant="outlined"
                  data-testid="dryrun-chip"
                />
              )}
              {data.jobUrl && (
                <a
                  href={data.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub-Run öffnen"
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <OpenInNewIcon fontSize="small" />
                    <Typography variant="body2">GitHub-Run</Typography>
                  </Box>
                </a>
              )}
            </Box>

            {data.status === 'completed' && data.dryRun && (
              // NC-985 (F3): a completed dry-run captured no image and is not
              // promotable — say so, and suppress the promotion banner below.
              <Alert severity="info" sx={{ mb: 2 }} data-testid="dryrun-note">
                Dry-Run (Validate-only): Es wurde kein Image erzeugt. Dieser Lauf ist nicht
                promotbar — für ein promotbares Image einen regulären Build ohne Dry-Run starten.
              </Alert>
            )}

            {data.status === 'completed' && data.environment === 'staging' && !data.promotedAt && !data.dryRun && (
              isStagingOnly(data.workloadId) ? (
                // NC-984 (F2): staging-only workloads are never promotable.
                // Say so explicitly instead of showing the generic "läuft über einen
                // Admin" banner, which wrongly implies promotion is possible.
                <Alert severity="info" sx={{ mb: 2 }} data-testid="staging-only-banner">
                  Dieses Image ({data.workloadId}) bleibt in der staging-Gallery und wird nicht
                  nach production promotet. Für Trainings-Images (CAM/BIM) erfolgt die Promotion
                  über einen Admin.
                </Alert>
              ) : (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    hasRole('admin') && isPromotable(data.workloadId) ? (
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<PromoteIcon />}
                        onClick={() => {
                          setPromoteError(null);
                          setPromoteDialogOpen(true);
                        }}
                      >
                        Promote to Production
                      </Button>
                    ) : undefined
                  }
                >
                  Image liegt in der staging-Gallery. Promotion auf production läuft über einen
                  Admin.
                </Alert>
              )
            )}

            {data.promotedAt && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Image wurde am {formatTimestamp(data.promotedAt)} als production markiert
                {data.promotedByEmail ? ` von ${data.promotedByEmail}` : ''}.
                {data.promotionJobUrl && (
                  <>
                    {' '}
                    <a
                      href={data.promotionJobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Promotion GitHub-Run öffnen"
                    >
                      GitHub-Run
                    </a>
                  </>
                )}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Workload
                </Typography>
                <Typography variant="body1">{data.workloadId}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Angefordert von
                </Typography>
                <Typography variant="body1">
                  {data.requestedByEmail || data.requestedBy}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Angefordert am
                </Typography>
                <Typography variant="body1">{formatTimestamp(data.requestedAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Gestartet
                </Typography>
                <Typography variant="body1">{formatTimestamp(data.startedAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Beendet
                </Typography>
                <Typography variant="body1">{formatTimestamp(data.completedAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Dauer
                </Typography>
                <Typography variant="body1">{formatDuration(data.durationSeconds)}</Typography>
              </Grid>
              {data.galleryImageVersion && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Gallery Image Version
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {data.galleryImageVersion}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {data.status === 'failed' && data.errorSummary && (
              <Box mt={3}>
                <Alert severity="error">
                  <Typography variant="body2" fontWeight="bold">
                    {data.errorSummary}
                  </Typography>
                  {data.errorTail && (
                    <Accordion sx={{ mt: 1, boxShadow: 'none', background: 'transparent' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="error-tail-content">
                        <Typography variant="body2">Letzte Log-Zeilen anzeigen</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            m: 0,
                            p: 1,
                            background: 'rgba(0,0,0,0.05)',
                            borderRadius: 1,
                            maxHeight: 360,
                            overflow: 'auto',
                          }}
                          aria-label="error-tail"
                        >
                          {data.errorTail}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Falls die Meldung unklar ist, frag deinen Admin.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 4 — admin promotion confirmation */}
      {data && (
        <Dialog
          open={promoteDialogOpen}
          onClose={() => {
            if (!promoteSubmitting) setPromoteDialogOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          aria-labelledby="promote-dialog-title"
        >
          <DialogTitle id="promote-dialog-title">
            Image-Version {data.galleryImageVersion} promoten
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Markiert die Gallery-Image-Version <strong>{data.galleryImageVersion}</strong> für{' '}
              <strong>{data.workloadId}</strong> als production-approved. Tutoren können diese
              Version dann gezielt für neue Trainings-Runs auswählen.
            </DialogContentText>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Die Aktion läuft als GitHub-Workflow (≈ 1 Minute) und ist über das Audit-Log
              verfolgbar.
            </Alert>
            {promoteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {promoteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPromoteDialogOpen(false)}
              disabled={promoteSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={promoteSubmitting}
              startIcon={promoteSubmitting ? <CircularProgress size={16} color="inherit" /> : <PromoteIcon />}
              onClick={async () => {
                setPromoteSubmitting(true);
                setPromoteError(null);
                try {
                  await imageBuildsApi.promote(data.id, data.workloadId);
                  setPromoteDialogOpen(false);
                  refetch();
                } catch (err) {
                  setPromoteError(
                    err instanceof Error ? err.message : 'Promotion konnte nicht gestartet werden.'
                  );
                } finally {
                  setPromoteSubmitting(false);
                }
              }}
            >
              {promoteSubmitting ? 'Starte…' : 'Promote bestätigen'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
